import { useMemo, useState } from 'react';
import {
  formatDuration,
  formatPercent,
  formatPercentTenths,
  percentTenths,
} from './dashboardSummaryUtils';
import { EXPECTED_HOURS_PER_WEEK, computeUtilization } from './utilizationMath';
import styles from './DashboardSummaryCards.module.scss';

const RUN_COLOR = '#2f8f5b';
const DOWN_COLOR = '#d64545';
const OVER_COLOR = '#e07b00';

// Fill of a utilization bar, split into its run time (green) and downtime (red) parts.
function StackedBarFill({ widthPct, runMinutes, downMinutes }) {
  const total = runMinutes + downMinutes;
  const downPct = total > 0 ? (downMinutes / total) * 100 : 0;
  return (
    <div
      className={`${styles.utilBarFill} ${styles.utilBarStack}`}
      style={{ width: `${widthPct}%` }}
    >
      <div style={{ width: `${100 - downPct}%`, backgroundColor: RUN_COLOR }} />
      <div style={{ width: `${downPct}%`, backgroundColor: DOWN_COLOR }} />
    </div>
  );
}

function splitTitle(totalMinutes, downMinutes) {
  return `Run time ${formatDuration(totalMinutes - downMinutes)} · Down time ${formatDuration(downMinutes)}`;
}

function UtilizationCard({
  usageData,
  errorData,
  machineCount = 1,
  startDate,
  endDate,
  loading = false,
}) {
  const {
    totalUtilization,
    weeks,
    totalRunMinutes,
    totalExpectedMinutes,
    periodWeeks,
    machineWeeks,
  } = useMemo(
    () => computeUtilization({ usageData, errorData, machineCount, startDate, endDate }),
    [usageData, errorData, machineCount, startDate, endDate]
  );

  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const toggleWeek = ws => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(ws)) {
        next.delete(ws);
      } else {
        next.add(ws);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className={styles.summaryPanel}>
        <div className={styles.loadingMessage}>Loading utilization...</div>
      </div>
    );
  }

  const hasData = totalRunMinutes > 0;
  // Utilization is measured on total tracked time, which already contains the
  // downtime (errors are only recorded while a machine is in use). Split it the
  // same way the Run Time Ratio card does so both cards agree.
  const downMinutes = Math.min(errorData?.total_minutes || 0, totalRunMinutes);
  const runMinutes = totalRunMinutes - downMinutes;
  // Percentages of expected hours: down time is the remainder of the rounded
  // total and run time, so run + down always adds up to the total shown.
  const totalTenths = percentTenths(totalUtilization);
  const runTenths =
    totalExpectedMinutes > 0
      ? Math.min(percentTenths(runMinutes / totalExpectedMinutes), totalTenths)
      : 0;
  const hasExpected = totalExpectedMinutes > 0;
  const totalPct = hasExpected ? formatPercentTenths(totalTenths) : '–';
  const runPct = hasExpected ? formatPercentTenths(runTenths) : '–';
  const downPct = hasExpected ? formatPercentTenths(totalTenths - runTenths) : '–';
  const donutRadius = 54;
  const donutCircumference = 2 * Math.PI * donutRadius;
  const isOverUtil = totalUtilization > 1;
  // Outer ring is filled up to the utilization (capped at 100%) and split into a
  // run time arc followed by a downtime arc in proportion to the tracked total.
  const outerFillRatio = isOverUtil ? 1 : totalUtilization;
  const outerFillLength = donutCircumference * outerFillRatio;
  const downArcLength = totalRunMinutes > 0 ? outerFillLength * (downMinutes / totalRunMinutes) : 0;
  const runArcLength = outerFillLength - downArcLength;
  // Inner ring shows excess fraction (e.g. 0.45 for 145%)
  const innerRadius = 38;
  const innerCircumference = 2 * Math.PI * innerRadius;
  const excessRatio = isOverUtil ? Math.min(totalUtilization - 1, 1) : 0;
  const innerStrokeOffset = innerCircumference * (1 - excessRatio);

  // Bar chart scale: ensure bars representing >100% extend visibly past the 100% mark
  const maxWeekUtil = weeks.length > 0 ? Math.max(...weeks.map(w => w.utilization)) : 1;
  const barScale = Math.max(1.2, maxWeekUtil * 1.05);

  return (
    <div className={styles.summaryPanel}>
      <h3 className={styles.panelTitle}>
        Utilization
        <span className={styles.panelSubtitle}>
          {`Expected ${EXPECTED_HOURS_PER_WEEK}h / week`}
        </span>
      </h3>
      <p className={styles.chartSubtitle}>
        Total tracked time (run time + down time) as a fraction of the expected{' '}
        {EXPECTED_HOURS_PER_WEEK}-hour weekly operational hours. Values above 100% indicate the
        machine ran more than expected.
      </p>

      {hasData ? (
        <>
          {/* Overall donut */}
          <div className={styles.donutLayout}>
            <div className={styles.donutChart}>
              <svg
                className={styles.donutSvg}
                viewBox="0 0 140 140"
                role="img"
                aria-label={`Overall utilization ${totalPct}: run time ${runPct}, down time ${downPct}`}
              >
                {/* Outer ring */}
                <circle cx="70" cy="70" r={donutRadius} className={styles.donutTrack} />
                <circle
                  cx="70"
                  cy="70"
                  r={donutRadius}
                  className={`${styles.donutUsage} ${styles.donutSegment}`}
                  style={{ stroke: RUN_COLOR }}
                  strokeDasharray={`${runArcLength} ${donutCircumference}`}
                />
                {downArcLength > 0 && (
                  <circle
                    cx="70"
                    cy="70"
                    r={donutRadius}
                    className={`${styles.donutUsage} ${styles.donutSegment}`}
                    style={{ stroke: DOWN_COLOR }}
                    strokeDasharray={`${downArcLength} ${donutCircumference}`}
                    strokeDashoffset={-runArcLength}
                  />
                )}
                {/* Inner overflow ring — only visible when > 100% */}
                {isOverUtil && (
                  <>
                    <circle cx="70" cy="70" r={innerRadius} className={styles.donutInnerTrack} />
                    <circle
                      cx="70"
                      cy="70"
                      r={innerRadius}
                      className={styles.donutOverflow}
                      strokeDasharray={innerCircumference}
                      strokeDashoffset={innerStrokeOffset}
                    />
                  </>
                )}
              </svg>
              <div className={styles.donutCenter}>
                <span className={styles.donutValue}>{totalPct}</span>
                <span className={styles.donutLabel}>utilization</span>
              </div>
            </div>

            <div className={styles.chartMetrics}>
              <div className={styles.metricItem}>
                <span
                  className={styles.metricSwatch}
                  style={{
                    background: `linear-gradient(90deg, ${RUN_COLOR} 50%, ${DOWN_COLOR} 50%)`,
                  }}
                />
                <div className={styles.metricText}>
                  <span className={styles.metricLabel}>
                    Total Time
                    {startDate && endDate && (
                      <span className={styles.metricLabelSub}>
                        {` (${startDate} – ${endDate})`}
                      </span>
                    )}
                  </span>
                  <span className={styles.metricValue}>
                    {formatDuration(totalRunMinutes)} · {totalPct}
                    <span className={styles.metricFormula}>(run time + down time)</span>
                  </span>
                </div>
              </div>
              <div className={styles.metricSplit}>
                <div className={styles.metricItem}>
                  <span className={styles.metricSwatch} style={{ backgroundColor: RUN_COLOR }} />
                  <div className={styles.metricText}>
                    <span className={styles.metricLabel}>Run Time</span>
                    <span className={styles.metricValue}>
                      {formatDuration(runMinutes)} · {runPct}
                    </span>
                  </div>
                </div>
                <div className={styles.metricItem}>
                  <span className={styles.metricSwatch} style={{ backgroundColor: DOWN_COLOR }} />
                  <div className={styles.metricText}>
                    <span className={styles.metricLabel}>Down Time</span>
                    <span className={styles.metricValue}>
                      {formatDuration(downMinutes)} · {downPct}
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.metricItem}>
                <span className={styles.metricSwatch} style={{ backgroundColor: '#aab8c2' }} />
                <div className={styles.metricText}>
                  <span className={styles.metricLabel}>Expected Operational</span>
                  <span className={styles.metricValue}>
                    {formatDuration(Math.round(totalExpectedMinutes))}
                    <span className={styles.metricFormula}>
                      {`(${machineCount > 0 ? machineCount : 1} ${machineCount > 1 ? 'machines' : 'machine'} × ${periodWeeks.toFixed(1)} weeks × ${EXPECTED_HOURS_PER_WEEK} hours/week)`}
                    </span>
                  </span>
                </div>
              </div>
              <div
                className={styles.metricCallout}
                style={isOverUtil ? { backgroundColor: '#fff4e0', color: '#7a4800' } : {}}
              >
                {isOverUtil
                  ? `Machine ran ${formatPercentTenths(totalTenths - 1000)} beyond expected operational hours`
                  : `${formatPercentTenths(1000 - totalTenths)} capacity unused in this period`}
              </div>
            </div>
          </div>

          {/* Weekly bar chart */}
          {weeks.length > 0 && (
            <div className={styles.utilWeeklySection}>
              <div className={styles.utilWeeklyTitle}>Weekly Breakdown</div>
              <div className={styles.utilBars}>
                {weeks.map(week => {
                  const isOver = week.utilization > 1;
                  const barWidthPct = Math.min((week.utilization / barScale) * 100, 100);
                  const hundredLinePct = (1 / barScale) * 100;
                  const machineRows = machineWeeks[week.weekStart] || [];
                  const isExpanded = expandedWeeks.has(week.weekStart);
                  const canExpand = machineRows.length > 0;

                  return (
                    <div key={week.weekStart}>
                      <div
                        className={`${styles.utilBarRow}${canExpand ? ` ${styles.utilBarRowExpandable}` : ''}`}
                        onClick={canExpand ? () => toggleWeek(week.weekStart) : undefined}
                        role={canExpand ? 'button' : undefined}
                        aria-expanded={canExpand ? isExpanded : undefined}
                      >
                        <span className={styles.utilWeekLabel}>
                          {canExpand && (
                            <span className={styles.utilExpandToggle}>
                              {isExpanded ? '▾' : '▸'}
                            </span>
                          )}
                          {week.label}
                        </span>
                        <div
                          className={styles.utilBarTrackWrap}
                          title={splitTitle(week.runMinutes, week.downMinutes)}
                        >
                          <div className={styles.utilBarTrack}>
                            <StackedBarFill
                              widthPct={barWidthPct}
                              runMinutes={week.runMinutes - week.downMinutes}
                              downMinutes={week.downMinutes}
                            />
                          </div>
                          <div
                            className={styles.utilBarHundredMark}
                            style={{ left: `${hundredLinePct}%` }}
                          />
                        </div>
                        <span
                          className={styles.utilBarValue}
                          style={{ color: isOver ? OVER_COLOR : '#24323f' }}
                        >
                          {formatPercent(week.utilization)}
                        </span>
                      </div>
                      {isExpanded && (
                        <div className={styles.utilMachineRows}>
                          {machineRows.map(m => {
                            const mIsOver = m.utilization > 1;
                            const mBarWidthPct = Math.min((m.utilization / barScale) * 100, 100);
                            return (
                              <div key={m.machineId} className={styles.utilMachineRow}>
                                <span className={styles.utilMachineLabel} title={m.machineLabel}>
                                  {m.machineLabel}
                                </span>
                                <div
                                  className={styles.utilBarTrackWrap}
                                  title={splitTitle(m.runMinutes, m.downMinutes)}
                                >
                                  <div
                                    className={`${styles.utilBarTrack} ${styles.utilMachineBarTrack}`}
                                  >
                                    <StackedBarFill
                                      widthPct={mBarWidthPct}
                                      runMinutes={m.runMinutes - m.downMinutes}
                                      downMinutes={m.downMinutes}
                                    />
                                  </div>
                                  <div
                                    className={styles.utilBarHundredMark}
                                    style={{ left: `${hundredLinePct}%` }}
                                  />
                                </div>
                                <div className={styles.utilMachineValue}>
                                  <span style={{ color: mIsOver ? OVER_COLOR : '#24323f' }}>
                                    {formatPercent(m.utilization)}
                                  </span>
                                  <span className={styles.utilMachineRunTime}>
                                    {formatDuration(m.runMinutes)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className={styles.utilBarLegend}>
                <span className={styles.metricSwatch} style={{ backgroundColor: RUN_COLOR }} />
                <span className={styles.utilBarLegendText}>Run time</span>
                <span className={styles.metricSwatch} style={{ backgroundColor: DOWN_COLOR }} />
                <span className={styles.utilBarLegendText}>Down time</span>
                <span className={styles.utilBarLegendLine} />
                <span className={styles.utilBarLegendText}>
                  100% target ({EXPECTED_HOURS_PER_WEEK}h)
                </span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.noData}>No run time data</div>
      )}
    </div>
  );
}

export default UtilizationCard;
